// Whether the Phone view is on and where it is (Session BDZ; a true on/off toggle with a remembered state since Session BEA).
// A tiny external store, so the toggle in a header and the stage mounted beside the layout agree without a provider.
//
// THE STATE IS REMEMBERED ON THE DEVICE (localStorage, in a try and catch): a refresh with the Phone view on comes back with it on,
// at the page the frame was last on. The remembered thing is the person's CHOICE, not whether the stage is showing: the stage shows
// only for an admin on a window wider than 900px (PhoneViewHost), and a window narrowed past that line, or an account that does
// not qualify, hides it WITHOUT forgetting the choice. Only a press of the toggle (or Close) turns it off.
//
// The frame never writes this: a page inside the Phone view's frame reads it (the store loads there too) and changes nothing.
import { useSyncExternalStore } from "react";
import { isInPhonePreviewFrame, loadPhoneViewOn, normalisePhonePath, savePhoneViewOn } from "./phonePreview";

export interface PhoneViewState {
  open: boolean;
  /** The path the frame opens at. */
  path: string;
}

const DEFAULT_PATH = "/office";

function restore(): PhoneViewState {
  const saved = loadPhoneViewOn();
  return { open: saved.on, path: saved.path ?? DEFAULT_PATH };
}

let state: PhoneViewState = restore();
const listeners = new Set<() => void>();

function persist(): void {
  if (isInPhonePreviewFrame()) return;
  savePhoneViewOn({ on: state.open, path: state.path });
}

function emit(next: PhoneViewState): void {
  state = next;
  persist();
  listeners.forEach((l) => l());
}

export function openPhoneView(path: string): void {
  emit({ open: true, path });
}

export function closePhoneView(): void {
  if (state.open) emit({ ...state, open: false });
}

/** Turn it on at `path` when it is off, and off when it is on: the header control's whole behaviour. */
export function togglePhoneView(path: string): void {
  if (state.open) closePhoneView();
  else openPhoneView(path);
}

/**
 * Remember where the frame is now, so a refresh comes back to the same screen. Does not notify: the stage opens its frame once and
 * navigates it in place, so a changed path must never reload it. Only a path the frame may open is kept.
 */
export function notePhoneViewPath(path: string): void {
  if (typeof window === "undefined") return;
  const clean = normalisePhonePath(path, window.location.origin);
  if (!clean || clean === state.path) return;
  state = { ...state, path: clean };
  if (state.open) persist();
}

export function currentPhoneViewState(): PhoneViewState {
  return state;
}

/** For a test: take the state the device holds now, as a fresh page load would. */
export function reloadPhoneViewState(): void {
  state = restore();
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function usePhoneViewState(): PhoneViewState {
  return useSyncExternalStore(subscribe, currentPhoneViewState, currentPhoneViewState);
}
